/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react/macro'
import colors from '@sportninja/common/constants/appColors'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { formatCurrency } from '@sportninja/common/utils/utils'
import type { TableColumnsType } from 'antd'
import { Tag, Dropdown, Badge, Tooltip } from 'antd'
import { memo, useCallback, useMemo, useState } from 'react'
import { SelectInput } from '../../SelectInput'
import dayjs from 'dayjs'
import {
  InfoCircleOutlined,
  MoreOutlined,
  PlusCircleFilled,
  TagOutlined,
} from '@ant-design/icons'
import CreditCardClockIcon from 'src/components/ts-components/icons/CreditCardClockIcon'
import ComplimentaryIcon from 'src/components/ts-components/icons/ComplimentaryIcon'
import { RegistrationTableDivision } from 'src/pages/Schedule/PlayerRegistration/helpers'
import { formatShortDate } from '@sportninja/common/ts-utils/date-utils'
import useWindowSize from 'src/hooks/useWindowSize'
import { Link } from 'react-router-dom'

const registrationOptions = [
  {
    label: 'Submitted',
    value: 1,
  },
  {
    label: 'Pending',
    value: 2,
  },
  {
    label: 'Approved',
    value: 3,
  },
  {
    label: 'Approved w/ Conditions',
    value: 4,
  },
  {
    label: 'Cancelled',
    value: 5,
  },
]

const registrationOptionsSubmitted = [
  {
    label: 'Submitted',
    value: 1,
  },
  {
    label: 'Approved',
    value: 3,
  },
  {
    label: 'Approved w/ Conditions',
    value: 4,
  },
  {
    label: 'Cancelled',
    value: 5,
  },
]

const registrationOptionsPending = [
  {
    label: 'Pending',
    value: 2,
  },
  {
    label: 'Approved',
    value: 3,
  },
  {
    label: 'Approved w/ Conditions',
    value: 4,
  },
  {
    label: 'Cancelled',
    value: 5,
  },
]

const registrationOptionsApproved = [
  {
    label: 'Approved',
    value: 3,
  },
  {
    label: 'Cancelled',
    value: 5,
  },
]

const registrationOptionsApprovedWithConditions = [
  {
    label: 'Approved w/ Conditions',
    value: 4,
  },
  {
    label: 'Cancelled',
    value: 5,
  },
]

const registrationOptionsCancelled = [
  {
    label: 'Pending',
    value: 2,
  },
  {
    label: 'Cancelled',
    value: 5,
  },
]

/** Team data interface */
interface Team {
  id: string
  team_roster_id?: string
  uid?: string
  team_id: string
  name_full: string
  players_count: number
  division_id: string
  is_comp: boolean
  is_bucket: boolean
  team_registration_id: string
  registration_status: number
  billing_schedule: BillingScheduleItem[]
  schedules: Schedule[]
  is_coupon: boolean
  coupon?: {
    code: string
    discount_type: string
    discount: number
    discount_amount: number
  }
  total_paid: number
  total_due: number
  registered_at: string
  roster_players_count: number
  defer_payment: boolean
}

/** Billing schedule item interface */
interface BillingScheduleItem {
  uid: string
  user_type: 'player' | 'teamRep'
  user_id: string
  team_official_id: string
  payment_type: 'deposit' | 'installment'
  due_date: string
  subtotal: number
  taxtotal: number
  total: number
  paid: boolean
  currency: number
  charge?: {
    id: number
    stripe_id: string
    description: string
    paid_at: string
    amount: string
    paid: number
    refunded: number
  }
  single_payment: boolean
  display_subtotal: number
  refunded: boolean
  defer_payment: boolean
}

/** Schedule interface */
interface Schedule {
  name: string
}

/** Props for the table component */
interface TableProps {
  setDivision: (division: RegistrationTableDivision) => void
  setDivisionId: (id: string) => void
  setParentTeamRegistrationId: (id: string) => void
  setTeamId: (id: string) => void
  setTeamName: (name: string) => void
  divisions: RegistrationTableDivision[]
  scheduleId: string
  handleStatusChange: (status: number, id: string, record: Team) => void
  loadingTeamIds: string[]
  setIsRegistrationDetailsModalOpen: (isOpen: boolean) => void
  setRegistrationDetailId: (id: string) => void
  setRegistrationDetailDefaultTab: (key: '0' | '1') => void
  setBucketToEdit: (bucket: any) => void
  setBucketToDelete: (bucket: any) => void
  setIsBucketModalOpen: (isOpen: boolean) => void
  setBucketScheduleRegistrationId: (id: string) => void
  setIsPlayerAdminModalOpen: (isOpen: boolean) => void
  setScheduleRegistrationId: (id: string) => void
  isPlayerRegistrationEnabled?: boolean
}

/**
 * Memoized team status selector component
 */
const TeamStatusSelector = memo(
  ({
    status,
    teamId,
    onChange,
    disabled,
    options,
    isBucket = false,
  }: {
    status: number
    teamId: string
    onChange: (status: number, id: string) => void
    disabled: boolean
    options: { label: string; value: number }[]
    isBucket?: boolean
  }) => {
    // Get color based on status
    const getStatusColor = (s: number) => {
      switch (s) {
        case 1: // Submitted
          return colors.INFO_200
        case 2: // Pending
          return colors.WARNING_200
        case 3: // Approved
          return colors.SUCCESS_200
        case 4: // Approved w/ Conditions
          return colors.SUCCESS_200
        case 5: // Cancelled
          return colors.ERROR_200
        default:
          return colors.NEUTRAL_100
      }
    }

    const optionsWithBadge = options.map((opt) => ({
      ...opt,
      label: (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
          `}
        >
          <Badge
            color={getStatusColor(opt.value)}
            css={css`
              .ant-badge-status-dot {
                width: 8px;
                height: 8px;
              }
            `}
          />
          {opt.label}
        </div>
      ),
      value: opt.value, // Ensure value is passed through
    }))

    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
        `}
      >
        <SelectInput
          options={optionsWithBadge}
          // @ts-ignore
          value={status}
          onChange={(value) => onChange(Number(value), teamId)}
          label=''
          disabled={disabled}
          name='teamStatus'
          fieldErrors={[]}
          customError={{}}
          style={`
            width: 150px;
            @media (max-width: 768px) {
              width: 100%;
            }
            height: 32px !important;
            // if is bucket, set the opacity to 0 and hide the dropdown
            // remove the cursor and pointer events
            ${isBucket && 'opacity: 0; cursor: default; pointer-events: none;'}
          `}
        />
      </div>
    )
  }
)

TeamStatusSelector.displayName = 'TeamStatusSelector'

interface PaymentStatusProps {
  totalPaid: number
  hasPaymentDelayed: boolean
  currency: string
  hasCoupon: boolean
  code: string
  discountText: string
  discount_type: string
  deferPayment: boolean
  onOpenBilling?: () => void
}

/**
 * Memoized payment status component
 */
const PaymentStatus = memo(
  ({
    totalPaid,
    hasPaymentDelayed,
    currency,
    hasCoupon,
    code,
    discountText,
    discount_type,
    deferPayment,
    onOpenBilling,
  }: PaymentStatusProps) => (
    <div
      css={css`
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
      `}
    >
      {hasCoupon ? (
        <Tooltip
          title={`Coupon: ${code} - ${discountText} ${
            discount_type === 'percentage' ? 'off' : 'discount'
          }`}
        >
          <TagOutlined
            style={{ color: colors.PRIMARY, fontSize: 20 }}
            color={colors.PRIMARY}
          />
        </Tooltip>
      ) : null}
      {deferPayment ? (
        <Tooltip
          title={onOpenBilling ? 'Deferred Payment' : 'Deferred Payment'}
        >
          <span
            onClick={onOpenBilling}
            css={css`
              display: inline-flex;
              align-items: center;
              cursor: ${onOpenBilling ? 'pointer' : 'default'};
              &:hover {
                opacity: ${onOpenBilling ? '0.8' : '1'};
              }
            `}
          >
            <CreditCardClockIcon size={20} color={colors.PRIMARY} />
          </span>
        </Tooltip>
      ) : null}
      <span
        css={css`
          color: ${totalPaid === 0
            ? colors.NEUTRAL_100
            : hasPaymentDelayed
            ? colors.ERROR_LIGHT
            : colors.SUCCESS_GREEN};
        `}
      >
        {formatCurrency(totalPaid, currency)}
      </span>
    </div>
  )
)

PaymentStatus.displayName = 'PaymentStatus'

/**
 * Error boundary component for image loading
 */
const TeamImage = ({
  src,
  alt,
  className,
  isBucket = false,
}: {
  src: string
  alt: string
  className?: string
  isBucket?: boolean
}) => {
  const [error, setError] = useState(false)

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 8px;
        align-self: center;
      `}
    >
      <img
        css={css`
          width: 32px;
          height: 32px;
          background: ${colors.WHITE};
          border-radius: 8px;
          padding: ${!isBucket ? '2px' : '0'};
          object-fit: contain;
        `}
        src={
          error || !src
            ? '/images/generic_placeholders/placeholder_team.png'
            : src
        }
        alt={alt}
        className={className}
        onError={(e) => {
          // Cast event target to HTMLImageElement
          const img = e.target as HTMLImageElement
          // Clear the error handler to prevent infinite loops
          img.onerror = null
          img.src = '/images/generic_placeholders/placeholder_team.png'
          setError(true)
        }}
      />
    </div>
  )
}

/**
 * Enhanced billing schedule renderer with error handling
 */
const SafeBillingRenderer = ({
  billingSchedule,
  division,
  isComplimentary,
  isBucket = false,
}: {
  billingSchedule?: BillingScheduleItem[]
  division?: RegistrationTableDivision
  isComplimentary: boolean
  isBucket?: boolean
}) => {
  if (!division?.currency?.name) {
    return <span>Currency not available</span>
  }
  if (!billingSchedule?.length) {
    return (
      <span
        css={css`
          opacity: 0;
        `}
      >
        Feb 14, 2025
      </span>
    )
  }

  if (isBucket) {
    return (
      <p
        css={css`
          opacity: 0;
        `}
      >
        Draft
      </p>
    )
  }

  return (
    <BillingRenderer
      billingSchedule={billingSchedule}
      division={division}
      isComplimentary={isComplimentary}
    />
  )
}

/**
 * Renders the team column with logo and name
 * @param record - The team record
 * @param text - The team name text
 * @param isBucket - Whether this is a bucket/group
 * @param scheduleId - The schedule ID for linking
 * @returns A component displaying team logo and name with proper styling
 */
const TeamColumnRenderer = ({
  record,
  text,
  isBucket,
  scheduleId,
}: {
  record: Team
  text: string
  isBucket: boolean
  scheduleId: string
}) => {
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 8px;
        align-self: center;
        max-width: 150px;
      `}
    >
      <TeamImage
        src={
          isBucket ? '/images/bucket_image.png' : getImageThumbnailId(record)
        }
        alt={`${text} team logo`}
        className='team-logo'
        isBucket={isBucket}
      />
      {isBucket ? (
        <span
          css={css`
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            word-break: break-word;
          `}
        >
          {text}
        </span>
      ) : (
        <Link
          to={`/team/${record?.team_id}/roster?sn_schedule=${scheduleId}`}
          css={css`
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            word-break: break-word;
            &:hover {
              text-decoration: underline;
            }
          `}
        >
          {text}
        </Link>
      )}
    </div>
  )
}

/**
 * Renders the billing information with currency formatting
 */
const BillingRenderer = ({
  billingSchedule,
  isComplimentary,
}: {
  billingSchedule: BillingScheduleItem[]
  division?: RegistrationTableDivision
  isComplimentary: boolean
}) => {
  if (isComplimentary) {
    return <span>{'Complimentary'}</span>
  }
  const nextInstallmentToPay = billingSchedule?.find((item) => {
    return item.paid === false
  })
  const due = nextInstallmentToPay?.due_date
  const day = dayjs(due, 'YYYY-MM-DD')
  const isAfter = dayjs().isAfter(day)

  if (!nextInstallmentToPay) {
    return <span>{'-'}</span>
  }

  return (
    <span
      css={css`
        ${isAfter && `color: ${colors.ERROR_200};`}
      `}
    >
      {formatShortDate(day.format('YYYY-MM-DD'))}{' '}
    </span>
  )
}

/**
 * Creates the table columns configuration
 */
export const useTableColumns = ({
  divisions,
  scheduleId,
  handleStatusChange,
  loadingTeamIds,
  setIsRegistrationDetailsModalOpen,
  setRegistrationDetailId,
  setRegistrationDetailDefaultTab,
  setBucketToEdit,
  setBucketToDelete,
  setIsBucketModalOpen,
  setBucketScheduleRegistrationId,
  setIsPlayerAdminModalOpen,
  setParentTeamRegistrationId,
  setTeamId,
  setDivisionId,
  setScheduleRegistrationId,
  setTeamName,
  setDivision,
  isPlayerRegistrationEnabled,
}: TableProps): TableColumnsType<Team> => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 768, [width])

  const getRegistrationOptions = useCallback((status) => {
    switch (status) {
      case 1:
        return registrationOptionsSubmitted
      case 2:
        return registrationOptionsPending
      case 3:
        return registrationOptionsApproved
      case 4:
        return registrationOptionsApprovedWithConditions
      case 5:
        return registrationOptionsCancelled
      default:
        return registrationOptions
    }
  }, [])

  // @ts-ignore
  return useMemo(
    () => [
      {
        title: 'Team',
        dataIndex: 'name_full',
        key: 'name_full',
        fixed: true,
        render: (text: string, record: Team) => (
          <Tooltip title={text}>
            <div
              css={css`
                display: -webkit-box;
                -webkit-line-clamp: 2; /* Allow 2 lines of text */
                -webkit-box-orient: vertical;
                overflow: hidden;
                text-overflow: ellipsis; /* Add ellipsis for text overflow */
                line-height: 1.2em;
                max-height: 2.4em; /* 2 lines × line-height */
              `}
            >
              <TeamColumnRenderer
                record={record}
                text={text}
                isBucket={record.is_bucket}
                scheduleId={scheduleId}
              />
            </div>
          </Tooltip>
        ),
        filterMode: 'menu',
        filters: [],
        onFilter: (value, record: Team) =>
          String(record.name_full) === String(value),
        filterSearch: true,
        sorter: (a: Team, b: Team) => a.name_full.localeCompare(b.name_full),
      },
      ...(isPlayerRegistrationEnabled
        ? [
            {
              title: (
                <Tooltip title={'Registered Players'} zIndex={10005}>
                  <span>Regd.</span>
                </Tooltip>
              ),
              dataIndex: 'players_count',
              key: 'players_count',
              render: (_: unknown, record: Team) => {
                const division = divisions?.find(
                  (d) => d.id === record.division_id
                )
                if (record?.is_bucket) {
                  if (division?.registration?.max_players_per_bucket) {
                    return <p>{record?.players_count || 0}</p>
                  }
                  return <p>{record?.players_count || 0}</p>
                }
                return (
                  <p>
                    {record?.players_count || 0}
                    {division?.maxPlayers ? ` / ${division.maxPlayers}` : ''}
                  </p>
                )
              },
              filters: [
                { text: '0-5 players', value: '0-5' },
                { text: '6-10 players', value: '6-10' },
                { text: '11-15 players', value: '11-15' },
                { text: '16+ players', value: '16+' },
              ],
              onFilter: (value, record: Team) => {
                const count = record.players_count || 0
                const [min, max] = String(value).split('-').map(Number)
                if (max) {
                  return count >= min && count <= max
                }
                return count >= min
              },
            },
          ]
        : []),
      ...(true
        ? [
            {
              title: (
                <Tooltip title={'Roster Players'} zIndex={10005}>
                  <span>Plyrs.</span>
                </Tooltip>
              ),
              dataIndex: 'roster_players_count',
              key: 'roster_players_count',
              render: (_: unknown, record: Team) => {
                return <p>{record?.roster_players_count || 0}</p>
              },
              filters: [
                { text: '0-5 players', value: '0-5' },
                { text: '6-10 players', value: '6-10' },
                { text: '11-15 players', value: '11-15' },
                { text: '16+ players', value: '16+' },
              ],
              onFilter: (value, record: Team) => {
                const count = record.players_count || 0
                const [min, max] = String(value).split('-').map(Number)
                if (max) {
                  return count >= min && count <= max
                }
                return count >= min
              },
            },
          ]
        : []),
      {
        title: (
          <Tooltip title={'Registered At'} zIndex={10005}>
            <span>Regd. At</span>
          </Tooltip>
        ),
        dataIndex: 'registered_at',
        key: 'registered_at',
        width: 100, // Set fixed width
        sorter: {
          compare: (a: Team, b: Team) => {
            const dateA = a.registered_at
              ? new Date(a.registered_at).getTime()
              : 0
            const dateB = b.registered_at
              ? new Date(b.registered_at).getTime()
              : 0
            return dateA - dateB
          },
          // Disable the default tooltip
          multiple: 1,
        },
        render: (registeredAt: string, record: Team) => {
          if (record.is_bucket) {
            return (
              <p
                css={css`
                  opacity: 0;
                `}
              >
                -
              </p>
            )
          }

          if (!registeredAt) {
            return <p>-</p>
          }

          return <p>{formatShortDate(registeredAt)}</p>
        },
      },
      {
        title: 'Due',
        dataIndex: 'billing_schedule',
        key: 'billing_schedule',
        width: 100, // Set fixed width
        render: (billingSchedule: BillingScheduleItem[], record: Team) => {
          return (
            <SafeBillingRenderer
              billingSchedule={billingSchedule}
              isComplimentary={record.is_comp}
              division={divisions.find((d) => d.id === record.division_id)}
              isBucket={record.is_bucket}
            />
          )
        },
      },
      {
        title: (
          <Tooltip title='Deposit Paid'>
            <span>Dep. Paid</span>
          </Tooltip>
        ),
        dataIndex: 'billing_schedule',
        key: 'deposit_paid',
        width: 80, // Set fixed width
        render: (billingSchedule: BillingScheduleItem[], record: Team) => {
          if (record.is_comp || billingSchedule?.length === 0) {
            return (
              <Tag
                color={'green'}
                css={css`
                  opacity: 0;
                `}
              >
                Yes
              </Tag>
            )
          }

          const deposit = billingSchedule
            ?.filter((item) => item.user_type === 'teamRep')
            .find((item) => item.payment_type === 'deposit')

          if (!deposit) {
            return null
          }

          return (
            <Tag color={deposit?.paid ? 'green' : 'red'}>
              {deposit?.paid ? 'Yes' : 'No'}
            </Tag>
          )
        },
        filters: [
          { text: 'Paid', value: 'true' },
          { text: 'Not Paid', value: 'false' },
        ],
        onFilter: (value, record: Team) => {
          if (record.is_comp || !record.billing_schedule?.length) {
            return false
          }
          const deposit = record.billing_schedule
            ?.filter((item) => item.user_type === 'teamRep')
            .find((item) => item.payment_type === 'deposit')
          return String(deposit?.paid) === value
        },
      },
      {
        title: (
          <Tooltip title={'Outstanding'} zIndex={10005}>
            <span>Outstg.</span>
          </Tooltip>
        ),
        dataIndex: 'billing_schedule',
        key: 'outstanding',
        align: 'right',
        render: (billingSchedule: BillingScheduleItem[], record: Team) => {
          const division = divisions.find((d) => d.id === record?.division_id)
          if (record.is_bucket) {
            return (
              <span
                css={css`
                  opacity: 0;
                `}
              >
                $775.00 USD
              </span>
            )
          }
          if (record.is_comp) {
            return <span>{formatCurrency(0, division?.currency?.name)}</span>
          }
          const total =
            billingSchedule
              ?.filter((item) => item.user_type === 'teamRep')
              .reduce((acc, curr) => acc + curr.subtotal, 0) || 0
          const paid =
            billingSchedule
              ?.filter((item) => item.user_type === 'teamRep')
              .filter((item) => item.paid && !item.refunded)
              .reduce((acc, curr) => acc + curr.subtotal, 0) || 0

          return (
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: flex-end;
                gap: 8px;
              `}
            >
              <span>
                {formatCurrency(total - paid, division?.currency?.name)}
              </span>
            </div>
          )
        },
        sorter: {
          compare: (a: Team, b: Team) => {
            const getOutstanding = (team: Team) => {
              if (team.is_bucket || team.is_comp) {
                return 0
              }
              const total =
                team.billing_schedule
                  ?.filter((item) => item.user_type === 'teamRep')
                  .reduce((acc, curr) => acc + curr.subtotal, 0) || 0
              const paid =
                team.billing_schedule
                  ?.filter((item) => item.user_type === 'teamRep')
                  .filter((item) => item.paid)
                  .reduce((acc, curr) => acc + curr.subtotal, 0) || 0
              return total - paid
            }
            return getOutstanding(a) - getOutstanding(b)
          },
          // Disable the sorter tooltip
          multiple: 1,
        },
      },
      {
        title: 'Paid',
        dataIndex: 'billing_schedule',
        key: 'paid',
        align: 'right',
        render: (billingSchedule: BillingScheduleItem[], record: Team) => {
          const hasCoupon = record?.is_coupon === true
          const deferPayment = record?.defer_payment || false
          const { code, discount_type, discount, discount_amount } =
            record?.coupon || {}
          const division = divisions.find((d) => d.id === record?.division_id)
          const discountText =
            discount_type === 'percentage'
              ? `${discount}%`
              : formatCurrency(
                  discount_amount || discount || 0,
                  division?.currency?.name
                )

          if (record.is_bucket) {
            return (
              <span
                css={css`
                  opacity: 0;
                `}
              >
                $325.00 USD
              </span>
            )
          }

          if (record.is_comp) {
            return (
              <Tooltip title='Complimentary Registration' zIndex={10005}>
                <span
                  css={css`
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    cursor: default;
                  `}
                >
                  <ComplimentaryIcon size={20} color={colors.INFO_200} />
                </span>
              </Tooltip>
            )
          }

          let totalPaid = 0
          let hasPaymentDelayed = false

          billingSchedule
            ?.filter((item) => item.user_type === 'teamRep')
            .forEach((payment) => {
              if (payment.paid) {
                totalPaid += payment.display_subtotal
              } else if (dayjs(payment.due_date).isBefore(dayjs())) {
                hasPaymentDelayed = true
              }
            })

          return (
            <PaymentStatus
              totalPaid={totalPaid}
              hasPaymentDelayed={hasPaymentDelayed}
              currency={division?.currency?.name || ''}
              hasCoupon={hasCoupon}
              code={code || ''}
              discountText={discountText || ''}
              discount_type={discount_type || ''}
              deferPayment={deferPayment || false}
              onOpenBilling={() => {
                setRegistrationDetailId(record.id)
                setIsRegistrationDetailsModalOpen(true)
                setRegistrationDetailDefaultTab('1')
              }}
            />
          )
        },
      },
      {
        title: (
          <Tooltip title={'Total Outstanding'} zIndex={10005}>
            <span>Total Outstg.</span>
          </Tooltip>
        ),
        dataIndex: 'total_due',
        key: 'total_due',
        align: 'right',
        render: (_value, record: Team) => {
          if (record.is_bucket) {
            return (
              <span
                css={css`
                  opacity: 0;
                `}
              >
                $0.00 USD
              </span>
            )
          }
          const division = divisions.find((d) => d.id === record?.division_id)

          // if (record.is_comp) {
          //   return <span>--</span>
          // }
          return (
            <span>
              {formatCurrency(record?.total_due || 0, division?.currency?.name)}
            </span>
          )
        },
      },
      {
        title: 'Total Paid',
        dataIndex: 'total_paid',
        key: 'total_paid',
        align: 'right',
        render: (_value, record: Team) => {
          if (record.is_bucket) {
            return (
              <span
                css={css`
                  opacity: 0;
                `}
              >
                $0.00 USD
              </span>
            )
          }
          const division = divisions.find((d) => d.id === record?.division_id)
          // if (record.is_comp) {
          //   return <span>--</span>
          // }
          return (
            <span>
              {formatCurrency(
                record?.total_paid || 0,
                division?.currency?.name
              )}
            </span>
          )
        },
      },
      {
        title: 'Status',
        key: 'status',
        width: 130,
        render: (_: unknown, record: Team) => {
          return (
            <TeamStatusSelector
              status={record.registration_status}
              teamId={record.id}
              onChange={(status, id) => handleStatusChange(status, id, record)}
              disabled={loadingTeamIds.includes(record.id)}
              options={getRegistrationOptions(record.registration_status)}
              isBucket={record.is_bucket}
            />
          )
        },
        filters: registrationOptions.map((option) => ({
          text: option.label,
          value: option.value,
        })),
        onFilter: (value: number, record: Team) =>
          record.registration_status === value,
      },
      {
        title: '',
        key: 'action',
        width: 100, // Set fixed width
        render: (_: unknown, record: Team) => {
          if (!record.is_bucket) {
            return (
              <div
                css={css`
                  display: flex;
                  align-items: flex-end;
                  gap: 8px;
                  justify-content: center;
                `}
              >
                <Tooltip title='Add Player'>
                  <PlusCircleFilled
                    onClick={() => {
                      const division = divisions.find(
                        (d) => d.id === record.division_id
                      )
                      const team = division?.teams?.find(
                        (t) => t.team_roster_id === record.team_roster_id
                      )
                      setParentTeamRegistrationId(team?.id || '')
                      setTeamId(record?.team_id || '')
                      setTeamName(record?.name_full || '')
                      setDivisionId(division?.registration?.schedule_id || '')
                      setDivision(division || ({} as RegistrationTableDivision))
                      setScheduleRegistrationId(
                        division?.registration?.id || ''
                      )
                      setIsPlayerAdminModalOpen(true)
                    }}
                    css={css`
                      color: ${colors.PRIMARY};
                      font-size: 20px;
                      cursor: pointer;
                      opacity: 1;
                    `}
                  />
                </Tooltip>
                <InfoCircleOutlined
                  onClick={() => {
                    setRegistrationDetailId(record.id)
                    setIsRegistrationDetailsModalOpen(true)
                  }}
                  css={css`
                    color: ${colors.PRIMARY};
                    font-size: 20px;
                    cursor: pointer;
                  `}
                />
              </div>
            )
          }

          const items = [
            {
              key: 'edit',
              label: 'Edit',
              onClick: () => {
                setBucketScheduleRegistrationId(record?.id || '')
                setBucketToEdit(record)
                setIsBucketModalOpen(true)
              },
            },
            {
              key: 'delete',
              label: 'Delete',
              onClick: () => {
                setBucketToDelete(record)
                document.getElementById('delete-bucket-button')?.click()
              },
            },
          ]

          return (
            <div
              css={css`
                display: flex;
                gap: 8px;
                position: absolute;
                right: ${isMobile ? '32px' : '20px'};
                top: 16px;
              `}
            >
              <Tooltip title='Add Player'>
                <PlusCircleFilled
                  onClick={() => {
                    const division = divisions.find(
                      (d) => d.id === record.division_id
                    )
                    const team = division?.teams?.find(
                      (t) => t.team_roster_id === record.team_roster_id
                    )
                    setParentTeamRegistrationId(team?.id || '')
                    setTeamId(record?.team_id || '')
                    setTeamName(record?.name_full || '')
                    setDivisionId(division?.registration?.schedule_id || '')
                    setDivision(division || ({} as RegistrationTableDivision))
                    setScheduleRegistrationId(division?.registration?.id || '')
                    setIsPlayerAdminModalOpen(true)
                  }}
                  css={css`
                    color: ${colors.PRIMARY};
                    font-size: 20px;
                    cursor: pointer;
                    opacity: 1;
                  `}
                />
              </Tooltip>
              <Dropdown
                menu={{ items }}
                trigger={['click']}
                placement='bottomRight'
              >
                <MoreOutlined
                  css={css`
                    color: ${colors.PRIMARY};
                    font-size: 20px;
                    cursor: pointer;
                    opacity: 1;
                  `}
                />
              </Dropdown>
            </div>
          )
        },
      },
    ],
    [
      divisions,
      getRegistrationOptions,
      handleStatusChange,
      loadingTeamIds,
      scheduleId,
      isMobile,
    ]
  )
}
